import {
  clearAccessToken,
  clearRefreshToken,
  getAccessToken,
  getRefreshToken,
  setAccessToken,
} from "./graphqlApi";
import settings from "../config/settings";
import { refreshMutation } from "./mutations";
import { clearLocalUser, getLocalUser, setLocalUser } from "./localStorage";

import { createThirdwebClient } from "thirdweb";
import humanize from "humanize";
import { grey } from "@mui/material/colors";
import ColorHash from "color-hash";
export const client = createThirdwebClient({
  clientId: process.env.VITE_APP_CLIENT_ID,
});

export const toPrecision = (precision) => (nbr, _, __, powerOf10) =>
  nbr === 0
    ? 0
    : ((powerOf10 = 10 ** (precision - Math.ceil(Math.log10(Math.abs(nbr))))),
      Math.round(nbr * powerOf10) / powerOf10);

export const poll = async function (fn, fnCondition, ms) {
  let result = await fn();
  while (fnCondition(result)) {
    await wait(ms);
    result = await fn();
  }
  return result;
};

export const nativeToken = (netId) => {
  switch (netId) {
    case 1:
      return "0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2";
  }
};

export const debounce = (callback, wait) => {
  let timeoutId = null;
  return (...args) => {
    window.clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => {
      callback(...args);
    }, wait);
  };
};

export const wait = function (ms = 1000) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
};

export const refreshToken = async () => {
  let isNotLoggedIn = false;
  const { refreshToken, token } = await poll(
    () => {
      let auth = { refreshToken: getRefreshToken(), token: getAccessToken() };
      if (!auth.refreshToken) {
        isNotLoggedIn = true;
      }
      return auth;
    },
    ({ refreshToken, token }) => !refreshToken || !token,
    1000
  );

  if (isNotLoggedIn) {
    return token;
  }

  const result = await fetch(settings.apiUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      query: refreshMutation,
      variables: { input: { token, refreshToken } },
    }),
  });
  const data = await result.json();
  if (data.data?.refresh?.jwt) {
    let auth = data.data?.refresh;

    let updatePage = false;
    let user = getLocalUser();
    if (
      user.username !== auth.user.username ||
      user.id !== auth.user.id ||
      user.role !== auth.user.role
    )
      updatePage = true;

    setAccessToken(auth.jwt);
    setLocalUser(auth.user);

    if (updatePage) {
      window.dispatchEvent(
        new StorageEvent("storage", {
          key: "user",
          newValue: JSON.stringify(auth.user),
          storageArea: localStorage,
        })
      );
    }
    return auth.jwt;
  }
  clearAccessToken();
  clearRefreshToken();
  clearLocalUser();
  return null;
};

export const handleCopy = async (content) => {
  try {
    await navigator.clipboard.writeText(content);
  } catch (err) {
    console.error("Failed to copy text: ", err);
  }
};

export function stringToColor(string, theme) {
  let isDark = theme.palette.mode === "dark" ? true : false;
  const colorHash = new ColorHash({
    lightness: [isDark ? 0.45 : 0.4, isDark ? 0.6 : 0.5, isDark ? 0.7 : 0.5],
    saturation: [0.5, 0.5, 0.5],
  });
  return colorHash.hex(string);
}
export function stringAvatar(token, theme, color, size = 60, fontSize = 11) {
  let symbol = "N/A";
  if ((token.symbol.length > 6 || !token.symbol.length) && token.name) {
    let words = token.name.split(" ");

    if (words.length > 6) {
      symbol = "";
      for (let i = 0; i < 5; i++) {
        if (!words[i].length) continue;
        symbol += words[i][0].toUpperCase();
      }
    } else if (words.length > 0) {
      symbol = "";
      for (let i = 0; i < words.length; i++) {
        if (!words[i].length) continue;
        symbol += words[i][0].toUpperCase();
      }
    }
  } else {
    symbol = token.symbol || "N/A";
  }

  return {
    sx: {
      bgcolor: color,
      fontSize,
      color: "white",
      textShadow: [
        "0 0 3px black",
        "0 0 3px black",
        "0 0 3px black",
        "0 0 3px black",
      ],
      width: size,
      height: size,
    },
    children: symbol,
  };
}

export const minifyAddress = (address) => {
  if (address.length > 10) {
    return address.substr(0, 6) + ".." + address.substr(38);
  }
  return address;
};

export const buildDateString = (token, contractState, marketState) => {
  let timestamp;
  let failed = false;

  if (marketState && marketState !== "ALL")
    switch (marketState) {
      case "LIQUIDITY":
        if (token.liquidityAt > 0) {
          timestamp = token.liquidityAt;
          break;
        }
        failed = true;
      default:
        timestamp = token.createdAt;
    }
  else
    switch (contractState) {
      case "TOKEN_SOCIALS":
        if (token.socialsAt > 0) {
          timestamp = token.socialsAt;
          break;
        }
        failed = true;
      case "CODE_VERIFIED" || failed:
        if (token.verifiedAt > 0) {
          timestamp = token.verifiedAt;
          break;
        }
        failed = true;
      case "CONFIRMED" || failed:
        if (token.confirmedAt > 0) {
          timestamp = token.confirmedAt;
          break;
        }
        failed = true;
      case "DROPPED" || failed:
        if (token.droppedAt > 0) {
          timestamp = token.droppedAt;
          break;
        }
      case "PENDING" || failed:
        if (token.seenAt > 0) {
          timestamp = token.seenAt;
          break;
        }
      default:
        //TODO get latest contractState
        timestamp = token.createdAt;
    }

  let dateString = humanize
    .relativeTime(timestamp / 1000)
    .replace("about ", "");

  let createdAt = new Date(timestamp);
  let now = new Date();

  if (createdAt.getDay() < now.getDay()) {
    dateString +=
      " " +
      createdAt.toLocaleDateString() +
      " " +
      createdAt.toLocaleTimeString();
  } else {
    dateString += " " + createdAt.toLocaleTimeString();
  }

  return dateString;
};

export const chipColorStyles = (theme, tagColor) => ({
  color: tagColor,
  borderColor: tagColor,
  backgroundColor:
    theme.palette.mode === "dark"
      ? theme.palette.background.default
      : theme.palette.background.default,

  // textShadow: [
  //   "0 0 10px black",
  //   "0 0 10px black",
  //   "0 0 10px black",
  //   "0 0 10px black",
  // ],
  fontWeight: "bold",
});

export const countDecimals = function (value) {
  if (value % 1 != 0) return value.toString().split(".")[1].length;
  return 0;
};

export const CSVToArray = (strData, strDelimiter) => {
  // Check to see if the delimiter is defined. If not,
  // then default to comma.
  strDelimiter = strDelimiter || ",";

  // Create a regular expression to parse the CSV values.
  var objPattern = new RegExp(
    // Delimiters.
    "(\\" +
      strDelimiter +
      "|\\r?\\n|\\r|^)" +
      // Quoted fields.
      '(?:"([^"]*(?:""[^"]*)*)"|' +
      // Standard fields.
      '([^"\\' +
      strDelimiter +
      "\\r\\n]*))",
    "gi"
  );

  // Create an array to hold our data. Give the array
  // a default empty first row.
  var arrData = [[]];

  // Create an array to hold our individual pattern
  // matching groups.
  var arrMatches = null;

  // Keep looping over the regular expression matches
  // until we can no longer find a match.
  while ((arrMatches = objPattern.exec(strData))) {
    // Get the delimiter that was found.
    var strMatchedDelimiter = arrMatches[1];

    // Check to see if the given delimiter has a length
    // (is not the start of string) and if it matches
    // field delimiter. If id does not, then we know
    // that this delimiter is a row delimiter.
    if (strMatchedDelimiter.length && strMatchedDelimiter !== strDelimiter) {
      // Since we have reached a new row of data,
      // add an empty row to our data array.
      arrData.push([]);
    }

    var strMatchedValue;

    // Now that we have our delimiter out of the way,
    // let's check to see which kind of value we
    // captured (quoted or unquoted).
    if (arrMatches[2]) {
      // We found a quoted value. When we capture
      // this value, unescape any double quotes.
      strMatchedValue = arrMatches[2].replace(new RegExp('""', "g"), '"');
    } else {
      // We found a non-quoted value.
      strMatchedValue = arrMatches[3];
    }

    // Now that we have our value string, let's add
    // it to the data array.
    arrData[arrData.length - 1].push(strMatchedValue);
  }

  // Return the parsed data.
  return arrData;
};

export const colorPercent = (percent) => {
  if (percent > 10) return "error";
  else if (percent > 5) return "warning";
  else if (percent > 2) return "info";
  else return "success";
};
